
import React from 'react';
import { UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import type { Application } from '@/types/database.types';

interface EPLISurveyProps {
  register: UseFormRegister<Partial<Application>>;
  setValue: UseFormSetValue<Partial<Application>>;
  watch: UseFormWatch<Partial<Application>>;
}

export const EPLISurvey = ({ register, setValue, watch }: EPLISurveyProps) => {
  const hasStandaloneEPLI = watch('has_standalone_epli_policy');
  const hasRecentLayoffs = watch('has_recent_layoffs');
  const hasPlannedLayoffs = watch('has_planned_layoffs');

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium">EPLI History</h3>
      
      <div className="space-y-4">
        {/* Standalone EPLI Policy */}
        <div className="space-y-2">
          <Label htmlFor="has_standalone_epli_policy" className="font-medium">
            Does your business have a standalone EPLI policy?
            <span className="text-red-500">*</span>
          </Label>
          <RadioGroup 
            defaultValue={hasStandaloneEPLI ? 'true' : 'false'} 
            onValueChange={(value) => setValue('has_standalone_epli_policy', value === 'true')}
            className="flex space-x-4"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="true" id="epli-yes" />
              <Label htmlFor="epli-yes" className="font-normal">Yes</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="false" id="epli-no" />
              <Label htmlFor="epli-no" className="font-normal">No</Label>
            </div>
          </RadioGroup>
        </div>

        {/* Recent Layoffs */}
        <div className="space-y-2">
          <Label htmlFor="has_recent_layoffs" className="font-medium">
            Have you had any layoffs in the past 12 months?
            <span className="text-red-500">*</span>
          </Label>
          <RadioGroup 
            defaultValue={hasRecentLayoffs ? 'true' : 'false'} 
            onValueChange={(value) => setValue('has_recent_layoffs', value === 'true')}
            className="flex space-x-4"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="true" id="recent-layoffs-yes" />
              <Label htmlFor="recent-layoffs-yes" className="font-normal">Yes</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="false" id="recent-layoffs-no" />
              <Label htmlFor="recent-layoffs-no" className="font-normal">No</Label>
            </div>
          </RadioGroup>
        </div>

        {/* Layoff Details - shown only if recent layoffs is true */}
        {hasRecentLayoffs && (
          <>
            <div className="space-y-2">
              <Label htmlFor="layoff_percentage" className="font-medium">
                What percentage of your workforce was laid off?
                <span className="text-red-500">*</span>
              </Label>
              <Input
                id="layoff_percentage"
                type="number"
                min="0"
                max="100"
                step="0.1"
                {...register('layoff_percentage', { valueAsNumber: true })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="layoff_releases_percentage" className="font-medium">
                Of those employees laid off, what approximate percentage have signed releases?
                <span className="text-red-500">*</span>
              </Label>
              <Input
                id="layoff_releases_percentage"
                type="number"
                min="0"
                max="100"
                step="0.1"
                {...register('layoff_releases_percentage', { valueAsNumber: true })}
              />
            </div>
          </>
        )}

        {/* Planned Layoffs */}
        <div className="space-y-2">
          <Label htmlFor="has_planned_layoffs" className="font-medium">
            Do you anticipate or are you planning any layoffs in the next 12 months?
            <span className="text-red-500">*</span>
          </Label>
          <RadioGroup 
            defaultValue={hasPlannedLayoffs ? 'true' : 'false'} 
            onValueChange={(value) => setValue('has_planned_layoffs', value === 'true')}
            className="flex space-x-4"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="true" id="planned-layoffs-yes" />
              <Label htmlFor="planned-layoffs-yes" className="font-normal">Yes</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="false" id="planned-layoffs-no" />
              <Label htmlFor="planned-layoffs-no" className="font-normal">No</Label>
            </div>
          </RadioGroup>
        </div>

        {/* Planned Layoff Details - shown only if planned layoffs is true */}
        {hasPlannedLayoffs && (
          <>
            <div className="space-y-2">
              <Label htmlFor="planned_layoff_count" className="font-medium">
                What percentage of your workforce do you anticipate or plan on laying off?
                <span className="text-red-500">*</span>
              </Label>
              <Input
                id="planned_layoff_count"
                type="number"
                min="0"
                {...register('planned_layoff_count', { 
                  valueAsNumber: true 
                })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="planned_layoffs_details" className="font-medium">
                Please explain the rationale for the anticipated or planned layoffs?
                <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="planned_layoffs_details"
                rows={4}
                {...register('planned_layoffs_details')}
              />
            </div>
          </>
        )}

        {/* Termination History */}
        <div className="space-y-2">
          <Label htmlFor="voluntary_terminations" className="font-medium">
            How many voluntary terminations have you had in the past year?
            <span className="text-red-500">*</span>
          </Label>
          <Input
            id="voluntary_terminations"
            type="number"
            min="0"
            {...register('voluntary_terminations', { 
              valueAsNumber: true 
            })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="involuntary_terminations" className="font-medium">
            How many involuntary terminations have you had in the past year?
            <span className="text-red-500">*</span>
          </Label>
          <Input
            id="involuntary_terminations"
            type="number"
            min="0"
            {...register('involuntary_terminations', { 
              valueAsNumber: true 
            })}
          />
        </div>
      </div>
    </div>
  );
};
